/**
 * Sitzungsende nach 12 h **Ruhe** (ADR 0106, Wunsch Felix 2026-09-24).
 *
 * Liegt in `packages/shared`, weil ERP und Partnerportal dieselbe Regel
 * brauchen: Drifteten Karenz und Verlängerungsschwelle auseinander, fiele das
 * niemandem auf — es sind Zahlen, die man nur alle paar Monate sieht.
 *
 * Die Uhr ist das Token selbst: Der Server stellt es mit einem Leerlauf-Fenster
 * von 12 h aus (`TokenService`), und wer arbeitet, holt sich über
 * `POST /auth/session/refresh` ein frisches — damit rutscht der Ablauf nach
 * hinten. Wer 12 h nichts tut, läuft ab und landet auf der Anmeldung.
 *
 * Warum aus dem Token gelesen und nicht mitgezählt wird: Ein eigener Zähler im
 * Browser (localStorage „zuletzt aktiv") und der Server könnten auseinander
 * laufen — nach einem Schlaf des Geräts, über mehrere Tabs, nach einem
 * Serverwechsel. `exp`/`iat` im Token sind die Wahrheit, die der Server ohnehin
 * durchsetzt; alles hier ist nur die Oberfläche, die rechtzeitig darauf reagiert.
 *
 * Bewusst KEINE Bibliothek: Das ist ein Base64-Decode von zwei Zahlen. Die
 * Signatur wird hier nicht geprüft — das tut der Server bei jeder Anfrage. Ein
 * manipuliertes Token im eigenen localStorage schadet nur einem selbst.
 */

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/**
 * Base64url → Text, von Hand.
 *
 * Warum nicht `atob` oder `Buffer`: Dieses Paket wird von Browser UND Server
 * importiert und kommt deshalb ohne DOM- oder Node-Globals aus — ein `atob`
 * hier würde den Server-Build brechen (und umgekehrt).
 *
 * Bytes werden direkt als Zeichen gelesen. Für unsere Tokens ist das exakt
 * (`sub`, `iss`, `iat`, `exp` sind ASCII); ein exotisches Feld ergäbe Unsinn,
 * an dem `JSON.parse` scheitert — und ein unlesbares Token gilt als abgelaufen,
 * also der sichere Ausgang.
 */
function decodeBase64Url(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  let bits = 0;
  let bitCount = 0;
  let out = '';
  for (const char of base64) {
    if (char === '=') break;
    const value = BASE64_ALPHABET.indexOf(char);
    if (value < 0) throw new Error('kein Base64');
    bits = (bits << 6) | value;
    bitCount += 6;
    if (bitCount >= 8) {
      bitCount -= 8;
      out += String.fromCharCode((bits >> bitCount) & 0xff);
    }
  }
  return out;
}

/** Ein Token gilt als abgelaufen, sobald weniger als das hier übrig ist. */
const EXPIRY_GRACE_MS = 30_000;

/**
 * Ab diesem Alter wird bei Aktivität verlängert. 30 Minuten sind der Kompromiss:
 * selten genug, dass ein Arbeitstag keine Anfrage-Flut auslöst (≈16 Anfragen),
 * und weit genug vom 12-h-Fenster entfernt, dass ein schlafender Laptop oder
 * eine kurze Störung die Sitzung nicht kostet.
 */
export const RENEW_AFTER_MS = 30 * 60 * 1000;

export interface SessionTimes {
  /** Ausstellung (`iat`) in ms. */
  issuedAt: number;
  /** Ablauf (`exp`) in ms. */
  expiresAt: number;
}

/**
 * `iat`/`exp` aus einem JWT lesen. `null`, wenn das Token fehlt, keine drei
 * Teile hat, kein lesbares JSON enthält oder die Zeitangaben fehlen — der
 * Aufrufer behandelt das wie „keine Sitzung" statt zu raten.
 */
export function readSessionTimes(token: string | null): SessionTimes | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const decoded: unknown = JSON.parse(decodeBase64Url(parts[1]!));
    if (typeof decoded !== 'object' || decoded === null) return null;
    const { iat, exp } = decoded as { iat?: unknown; exp?: unknown };
    if (typeof iat !== 'number' || typeof exp !== 'number') return null;
    return { issuedAt: iat * 1000, expiresAt: exp * 1000 };
  } catch {
    return null;
  }
}

/**
 * Ist die Sitzung vorbei? Ein Token ohne lesbare Zeiten gilt als abgelaufen —
 * lieber einmal zu viel zur Anmeldung schicken als eine tote Oberfläche zeigen,
 * in der jede Aktion scheitert (genau das war der Zustand bis 2026-09-24).
 */
export function isSessionExpired(token: string | null, now: number = Date.now()): boolean {
  const times = readSessionTimes(token);
  if (!times) return true;
  return times.expiresAt - EXPIRY_GRACE_MS <= now;
}

/**
 * Soll bei Aktivität verlängert werden? Nur, wenn das Token alt genug ist —
 * sonst würde jeder Klick eine Anfrage auslösen.
 *
 * Ein bereits abgelaufenes Token wird NICHT verlängert: Der Server würde es
 * ohnehin mit 401 abweisen, und der Nutzer gehört dann zur Anmeldung.
 */
export function shouldRenewSession(token: string | null, now: number = Date.now()): boolean {
  const times = readSessionTimes(token);
  if (!times) return false;
  if (times.expiresAt <= now) return false;
  return now - times.issuedAt >= RENEW_AFTER_MS;
}

/**
 * Millisekunden bis zum Ablauf (nie negativ). `0` heißt „vorbei" — auch bei
 * einem unlesbaren Token, passend zu `isSessionExpired`.
 */
export function millisUntilExpiry(token: string | null, now: number = Date.now()): number {
  const times = readSessionTimes(token);
  if (!times) return 0;
  return Math.max(0, times.expiresAt - now);
}

// ---- Nächtlicher Schnitt (ADR 0107) ----------------------------------------

/**
 * Uhrzeit, zu der jede Sitzung endet — „jeder wird täglich ausgeloggt"
 * (Wunsch Felix, 2026-09-24).
 *
 * 03:00 statt einer Obergrenze ab Anmeldung: Eine 24-h-Grenze würde jemanden,
 * der sich dienstags um 14:00 anmeldet, mittwochs um 14:00 mitten im
 * Arbeitstag hinauswerfen — genau das, was das Leerlauf-Fenster (ADR 0106)
 * abschaffen sollte. Ein fester Zeitpunkt nachts trifft niemanden bei der
 * Arbeit und garantiert trotzdem, dass keine Sitzung einen Tag überlebt.
 */
export const DAILY_CUTOFF_HOUR = 3;

/**
 * Maßgebliche Zone. Bewusst fest und nicht die Zone des Geräts: Der Schnitt ist
 * eine Hausregel („nachts um drei"), keine Eigenschaft des Browsers — sonst
 * bekäme jeder seinen eigenen Zeitpunkt. Für das Partnerportal heißt das:
 * Partner in anderen Zeitzonen werden nach Österreichs Uhr abgemeldet
 * (bewusste Entscheidung, siehe ADR 0107).
 */
export const SESSION_TIME_ZONE = 'Europe/Berlin';

/** Leerlauf-Fenster: so lange gilt ein Token ohne Verlängerung (ADR 0106). */
export const IDLE_WINDOW_MS = 12 * 60 * 60 * 1000;

interface WallClock {
  year: number;
  month: number;
  day: number;
}

/** Wanduhr-Datum eines Zeitpunkts in der gegebenen Zone. */
function wallClockDate(instant: number, timeZone: string): WallClock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(instant));
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  return { year: value('year'), month: value('month'), day: value('day') };
}

/**
 * Abstand der Zone zu UTC an einem konkreten Zeitpunkt (in ms).
 *
 * Trick: Die Wanduhr-Angaben der Zone werden gelesen und so interpretiert, als
 * wären sie UTC — die Differenz zum echten Zeitpunkt IST der Abstand. Damit
 * braucht es keine Zeitzonen-Bibliothek und keine fest verdrahteten
 * Sommerzeit-Regeln; `Intl` kennt sie bereits.
 */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  // `hour` kann in manchen Umgebungen „24" für Mitternacht liefern.
  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour') % 24,
    value('minute'),
    value('second'),
  );
  return asUtc - instant;
}

/**
 * Der Zeitpunkt, an dem in der Zone eine bestimmte Wanduhr-Zeit steht.
 *
 * Zweimal genähert, weil der Zonen-Abstand selbst vom Zeitpunkt abhängt: Die
 * erste Schätzung (als wäre die Wanduhr UTC) liefert einen Abstand, der die
 * zweite schon richtig stellt — auch über eine Zeitumstellung hinweg.
 */
function instantAtWallClock(date: WallClock, hour: number, timeZone: string): number {
  const naive = Date.UTC(date.year, date.month - 1, date.day, hour, 0, 0);
  let instant = naive;
  for (let round = 0; round < 2; round += 1) {
    instant = naive - zoneOffsetMs(instant, timeZone);
  }
  return instant;
}

/**
 * Nächster Schnitt-Zeitpunkt nach `now`. Liegt die heutige Uhrzeit schon hinter
 * dem Schnitt, ist es der von morgen.
 */
export function nextDailyCutoff(
  now: number = Date.now(),
  hour: number = DAILY_CUTOFF_HOUR,
  timeZone: string = SESSION_TIME_ZONE,
): number {
  const today = instantAtWallClock(wallClockDate(now, timeZone), hour, timeZone);
  if (today > now) return today;
  // „Morgen" über einen Sprung von 24 h bestimmen: Ein Kalendertag kann in der
  // Zone 23 oder 25 Stunden haben, aber nie zwei Tage überspringen.
  return instantAtWallClock(wallClockDate(now + 24 * 60 * 60 * 1000, timeZone), hour, timeZone);
}

/**
 * Wie lange ein frisch ausgestelltes Token gilt: das FRÜHERE aus „12 h Ruhe"
 * und „nächster nächtlicher Schnitt". Zwei Regeln, ein Ablaufzeitpunkt — die
 * Oberfläche muss nichts davon wissen, sie liest nur `exp`.
 *
 * Praktisch heißt das: Vor 15:00 gewinnt fast immer das Leerlauf-Fenster,
 * danach der Schnitt. Wer um 02:50 noch arbeitet, hat zehn Minuten.
 */
export function sessionLifetimeMs(
  now: number = Date.now(),
  timeZone: string = SESSION_TIME_ZONE,
): number {
  const untilCutoff = nextDailyCutoff(now, DAILY_CUTOFF_HOUR, timeZone) - now;
  return Math.min(IDLE_WINDOW_MS, Math.max(0, untilCutoff));
}

/**
 * Endete diese Sitzung am nächtlichen Schnitt statt an der Ruhe?
 *
 * Erkennbar am Token selbst: Wurde es auf weniger als das volle Leerlauf-Fenster
 * ausgestellt, war der Schnitt der Grund. Damit kann die Anmeldeseite den
 * richtigen Satz zeigen, statt um 03:00 „12 Stunden ohne Aktivität" zu behaupten.
 */
export function endedByDailyCutoff(token: string | null): boolean {
  const times = readSessionTimes(token);
  if (!times) return false;
  // Eine Sekunde Toleranz: `iat`/`exp` sind auf Sekunden gerundet.
  return times.expiresAt - times.issuedAt < IDLE_WINDOW_MS - 1000;
}
